// @ts-nocheck

import { colors } from '@lmc/common/constants'
import type { ImportantContact } from '@lmc/ts-common'
import { theme } from '@lmc/ui-kit'
import { isArray } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { postJSON } from '../../common/services/api'
import { darken, lighten } from '../../LEGACY/colorUtils'
import { FormFieldLayout, Icon } from '../generic'
import Modal from '.'
import PopupModal from './PopupModal'

interface Props {
  importantContact: ImportantContact
  residentId: string
  index: number
  showToast: (message: { title: string; type: string }) => void
  fetchList: (list: string) => void
  close: () => void
  isOpen: boolean
  residentName: { first: string; last: string }
  features: { familyPaymentType: string }
  error?: boolean
}

export function InviteFamilyModal(props: Props) {
  const {
    importantContact,
    residentId,
    index,
    showToast,
    fetchList,
    close,
    isOpen,
    residentName,
    features: { familyPaymentType },
    error,
    ...formProps
  } = props

  const [comment, setComment] = useState('')
  const [isChecked, setIsChecked] = useState(false)

  const isMounted = useRef(false)
  const prevImportantContact = useRef<ImportantContact | undefined>(undefined)

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      prevImportantContact.current = importantContact
      return
    }
    if (!prevImportantContact.current && importantContact) {
      setComment(
        importantContact.familyComment ? importantContact.familyComment : ''
      )
      setIsChecked(false)
    }
    prevImportantContact.current = importantContact
  }, [importantContact])

  const handleSubmit = async () => {
    const url = `list/residents/${residentId}/invite-family/${index}`
    const json = { familyComment: comment }

    try {
      const resident = await postJSON({ url, json })
      handleComplete()
    } catch (error) {
      if (
        error.message.startsWith('E-mail already in use') ||
        error.userExists ||
        typeof error === 'string'
      ) {
        showToast({
          title: error.message,
          type: 'error'
        })
      } else {
        showToast({
          title: 'Something went wrong.',
          type: 'error'
        })
      }
    }
  }

  const handleComplete = () => {
    fetchList('residents')
    close()
    showToast({ title: 'Invited a Family User' })
  }

  const renderCheckbox = () => {
    let borderColor
    if (error) {
      borderColor = colors.red
    } else if (isChecked) {
      borderColor = theme.color.status.primary
    } else {
      borderColor = colors.grey
    }

    return (
      <CheckboxContainer>
        <div
          className="lmc-option-field"
          onClick={() => setIsChecked(!isChecked)}
          style={{ borderColor }}
        >
          <ValueContainer>
            <CheckboxLabel>
              {`I have received permission from ${importantContact.name.first} to send invite`}
            </CheckboxLabel>
          </ValueContainer>
          <Icon
            name={isChecked ? 'checkbox-on' : 'checkbox-off'}
            fill={isChecked ? theme.color.status.primary : null}
            size={24}
            style={{ flexShrink: 0 }}
          />
        </div>
      </CheckboxContainer>
    )
  }

  if (!isOpen) return null

  return (
    <PopupModal
      close={close}
      confirmText="Send Invite"
      title="Invite to Family App"
      buttonColour="success"
      buttonDisabled={!isChecked}
      buttonIcon="MailOutlined"
      onConfirm={handleSubmit}
      supportArticle="https://support.logmycare.co.uk/"
      footerStyle={{ justifyContent: 'space-between' }}
      {...formProps}
    >
      <Subheading key="subheading">
        {`An email will be sent to ${importantContact.name.first}
                    ${importantContact.name.last} with instructions on how to get
                    set up with access to ${residentName.first} ${residentName.last}'s information.`}
      </Subheading>
      {familyPaymentType === 'family' && (
        <Subheading>{PRICING_NOTE}</Subheading>
      )}
      <InfoContainer style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex' }}>
          <LabelContainer> Name </LabelContainer>
          <div>
            {' '}
            {`${importantContact.name.first} ${importantContact.name.last}`}{' '}
          </div>
        </div>
        {importantContact.lpa && isArray(importantContact.lpaTypes) && (
          <div>
            {importantContact.lpaTypes.map((type) => (
              <BadgeLPA key={type}>LPA {type}</BadgeLPA>
            ))}
          </div>
        )}
      </InfoContainer>
      <InfoContainer style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex' }}>
          <LabelContainer> Relationship </LabelContainer>
          {importantContact.relationship ? (
            <div> {`${importantContact.relationship}`} </div>
          ) : (
            <NoInfo> No information entered </NoInfo>
          )}
        </div>
        {importantContact.relationship &&
          (importantContact.type === 'Professional' ? (
            <BadgeProfessional>Professional</BadgeProfessional>
          ) : (
            <BadgePersonal>Personal</BadgePersonal>
          ))}
      </InfoContainer>
      <InfoContainer>
        <LabelContainer> Email </LabelContainer>
        <div> {`${importantContact.email}`} </div>
      </InfoContainer>
      <FieldContainer>
        <FormFieldLayout
          label="Why are you inviting them?"
          layout="column"
          labelNote="- Optional"
          labelStyle={{ fontWeight: 'bold' }}
        >
          <StyledTextarea
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            value={comment}
          />
        </FormFieldLayout>
      </FieldContainer>
      {renderCheckbox()}
    </PopupModal>
  )
}

const MODAL_ID = 'inviteFamily'
const PRICING_NOTE =
  'After their one month free trial, they will receive a monthly charge of £9.99 (including VAT).'

export default Modal(InviteFamilyModal, MODAL_ID)

const Badge = styled.div`
  align-self: flex-start;
  border-radius: 15px;
  display: inline-block;
  float: right;
  font-size: 12px;
  font-weight: bold;
  padding: 3px 12px;
`

const BadgeLPA = styled(Badge)`
  background: ${colors.grey};
  color: ${colors.white};
  margin-left: 10px;
`

const BadgePersonal = styled(Badge)`
  background: ${lighten(colors.purple, 80)};
  color: ${darken(colors.purple, 30)};
`

const BadgeProfessional = styled(Badge)`
  background: ${lighten(colors.darkblue, 80)};
  color: ${darken(colors.darkblue, 30)};
`

const CheckboxContainer = styled.div`
  display: flex;
`

const CheckboxLabel = styled.span`
  align-self: center;
  display: block;
  font-weight: 600;
  margin-right: 5px;
`

const FieldContainer = styled.div`
  padding-bottom: 15px;
  padding-top: 5px;
`

const InfoContainer = styled.div`
  align-items: center;
  display: flex;
  flex: row;
  padding-bottom: 20px;
`

const LabelContainer = styled.div`
  font-weight: bold;
  min-width: 120px;
`

const NoInfo = styled.div`
  align-self: flex-end;
  color: ${colors.black};
  font-size: 12px;
  font-style: italic;
`

const StyledTextarea = styled.textarea`
  border-color: ${colors.grey};
  border-radius: 5px;
  padding: 5px 10px 6px 10px;
  resize: none;
  width: 100%;

  &:hover {
    border-color: ${theme.color.status.primary};
  }

  &:focus {
    border-color: ${theme.color.status.primary};
    outline: 0;
  }
`

const Subheading = styled.div`
  font-size: 12px;
  padding-bottom: 20px;
`

const ValueContainer = styled.div`
  align-items: center;
  display: flex;
`
